import React, { useState } from 'react';
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, FormHelperText } from '@mui/material';
import { useTranslationContext } from '../../../../../../hooks/translations/translationsProvider';
import { WHITE, GREEN_LOGO, GREEN_MAIN } from '../../../../../../theme/colors';
import { Trans } from 'react-i18next';

interface CreditFormProps {
  onSubmit: (amount: number, info: string) => Promise<void>;
  userName?: string; // Add userName prop
  currentBalance?: number; // Add current balance prop for validation
}

const TokensForm: React.FC<CreditFormProps> = ({ onSubmit, userName, currentBalance = 0 }) => {
  const [amount, setAmount] = useState<string>('');
  const [info, setInfo] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslationContext();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    await onSubmit(parseFloat(amount), info);
    setAmount('');
    setInfo('');
  };

  const isNegative = parseFloat(amount) < 0;
  const displayAmount = Math.abs(parseFloat(amount));
  
  // Validation for negative amounts
  const amountValue = parseFloat(amount) || 0;
  const wouldResultInNegative = isNegative && Math.abs(amountValue) > currentBalance;
  const hasValidationError = wouldResultInNegative;
  const validationMessage = wouldResultInNegative 
    ? t("Non si possono sottrarre più tokens di quelli disponibili. Saldo disponibile: {{balance}} ASSO", { balance: currentBalance.toFixed(2) })
    : "";

  const isFormValid = amount && !isNaN(parseFloat(amount)) && parseFloat(amount) !== 0 && !hasValidationError;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={6}>
          <TextField
              label={t("Token ASSO da Aggiungere/Rimuovere")}
              type="number"
              value={amount}
              onChange={handleAmountChange}
              fullWidth
              margin="normal"
              error={hasValidationError}
              helperText={hasValidationError ? validationMessage : t("Inserisci un numero positivo se vuoi aggiungere Tokens altrimenti un numero negativo per rimuovere Tokens")}
              sx={{ 
                mb: 3,
                '& .MuiFormHelperText-root': {
                  color: hasValidationError ? 'error.main' : GREEN_MAIN
                }
              }}
          />
        </Grid>
      </Grid>
      
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={6}>
          <TextField
              label={t("Informazioni relative all'operazione")}
              multiline
              rows={3} 
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder={t("Informazioni relative all'operazione")}
              InputProps={{
                  sx: {
                      resize: "vertical", // Permette il ridimensionamento solo in verticale
                      overflow: "auto", // Aggiunge lo scroll se necessario
                  },
              }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }} justifyContent="center">
        <Grid item xs={12} sm={4} md={3}>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!isFormValid}
            sx={{
              margin:"10px 0 0 0",
              height: '35px',
              backgroundColor: GREEN_LOGO,
              color: WHITE,
              '&:hover': {
                backgroundColor: GREEN_MAIN,
              }
            }}
          >
            {t("Update Credits")}
          </Button>
        </Grid>
      </Grid>

      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: `1px solid ${GREEN_LOGO}30`,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            color: GREEN_MAIN, 
            fontWeight: 'bold',
            fontSize: '1.4rem',
            textAlign: 'center',
            pb: 1
          }}
        >
          {t("Conferma Aggiornamento Crediti")}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <DialogContentText 
            sx={{ 
              color: GREEN_MAIN,
              fontSize: '0.95rem',
              textAlign: 'center',
              lineHeight: 1.5
            }}
          >
            {isNegative ? (
              <Trans
                i18nKey="confirm-update-negative-balance"
                values={{
                  userName: userName || t("Utente"),
                  amount: displayAmount.toFixed(2)
                }}
                components={{
                  strong: <strong />
                }}
              />
            ) : (
              <Trans
                i18nKey="confirm-add-balance"
                values={{
                  userName: userName || t("Utente"),
                  amount: displayAmount.toFixed(2)
                }}
                components={{
                  strong: <strong />
                }}
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={() => setIsDialogOpen(false)} 
            sx={{
              backgroundColor: GREEN_LOGO,
              color: WHITE,
              '&:hover': {
                backgroundColor: GREEN_MAIN,
              },
              minWidth: '100px'
            }}
          >
            {t("Annulla")}
          </Button>
          <Button 
            onClick={handleConfirm} 
            sx={{
              backgroundColor: GREEN_LOGO,
              color: WHITE,
              '&:hover': {
                backgroundColor: GREEN_MAIN,
              },
              minWidth: '100px'
            }}
            autoFocus
          >
            {t("Conferma")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TokensForm;

